// @ts-strict-ignore
import React, { useState } from 'react';
import _ from 'lodash';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { SimpleSaveFormBuilder } from '@/hybrid/formbuilder/SimpleSaveFormBuilder.page';
import { FormElement } from '@/hybrid/formbuilder/formBuilder.module';
import { MAX_NAME_LENGTH } from '@/main/app.constants';
import { warnToast } from '@/hybrid/utilities/toast.utilities';
import { renameDisplay } from '@/hybrid/displays/displays.actions';
import { doTrack } from '@/track/track.service';

interface RenameDisplayModalProps {
  onClose: () => void;
  display: { id: string; name: string; type: string };
  displaysCount: number;
}

export const RenameDisplayModal: React.FunctionComponent<RenameDisplayModalProps> = ({
  onClose,
  display,
  displaysCount,
}) => {
  const { t } = useTranslation();

  const [displayName, setDisplayName] = useState(display.name);

  const formDefinition: FormElement[] = [
    {
      component: 'LabelFormComponent',
      name: 'renameDisplayLabel',
      value: 'DISPLAYS.MODAL.RENAME.BODY_DISPLAY',
      valueCustomParams: { ITEM_NAME: display.name },
    },
    {
      component: 'LabelFormComponent',
      name: 'renameDisplayWarning',
      value: 'DISPLAYS.MODAL.MULTIPLE_DISPLAY_WARNING',
      valueCustomParams: { SCALE_COUNT: displaysCount - 1 },
      extraClassNames: 'text-italic',
      includeIf: displaysCount > 1,
    },
    {
      component: 'FormControlFormComponent',
      name: 'renameDisplayName',
      value: displayName,
      extraClassNames: 'mtb10',
      size: 'lg',
      placeholder: 'DISPLAYS.MODAL.RENAME.NAME_PLACEHOLDER',
      maxLength: MAX_NAME_LENGTH.TOOL,
      onChange: (name: string) => setDisplayName(name),
      testId: 'renameDisplayNameInput',
      required: true,
    },
  ];

  /**
   * Renames the selected display
   *
   * @returns {Promise} that resolves when the update is complete
   */
  function trackAndRenameDisplay() {
    if (_.isEmpty(_.trim(displayName))) {
      warnToast({ messageKey: 'DISPLAYS.MODAL.RENAME.NAME_REQUIRED' });
      return Promise.resolve();
    }

    doTrack('Search results', 'Rename Display', `rename display item from ${display.name} to ${displayName}`);
    return renameDisplay(display, displayName).then(() => {
      onClose();
    });
  }

  return (
    <Modal show={true} onHide={onClose} animation={false} data-testid="renameDisplayModal">
      <Modal.Header closeButton={true}>
        <Modal.Title>{t('DISPLAYS.MODAL.RENAME.TITLE')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div data-testid="renameDisplayModalBody">
          <SimpleSaveFormBuilder
            testId="renameSimpleSaveFormBuilder"
            formDefinition={formDefinition}
            submitFn={trackAndRenameDisplay}
            closeFn={onClose}
            submitBtnLabel="RENAME"
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};
